/**
 * Login.js — PeoplesDock
 *
 * IMPROVED ERROR DISPLAY (this version)
 *   Previously, login errors only showed as a toast notification —
 *   fine for a quick "wrong password, try again" since the person
 *   just retypes and resubmits within a few seconds. But some errors
 *   (most notably the subscription-lock message from
 *   auth.controller.js: "Your company's subscription has expired.
 *   Please contact your company administrator to renew access.") are
 *   a hard stop that requires the person to go DO something else
 *   entirely (contact their admin) — a toast that auto-dismisses in a
 *   few seconds is easy to miss or forget the exact wording of.
 *
 *   Now every login error ALSO shows as a persistent inline Alert
 *   directly on the form, staying visible until the next login attempt
 *   or until manually dismissed. This applies uniformly to all login
 *   errors (wrong password, locked account, expired subscription,
 *   etc.) — no fragile string-matching on the message text needed,
 *   since the alert always mirrors whatever the backend actually said.
 *
 * ADDED (earlier, unchanged): UI Validation
 *   - Email: required, valid email format
 *   - Password: required
 *   - isInvalid / Form.Control.Feedback pattern (React-Bootstrap native)
 *   - Errors clear as user types (onChange)
 *   - Validate on blur
 *   - Submit blocked until all fields pass
 *
 * Everything else identical to your version.
 */
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Container, Row, Col, Form, Button, Card, InputGroup, Spinner, Alert } from 'react-bootstrap';
import { Mail, Lock, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/axiosConfig';
import { useAuth } from '../../context/AuthContext';
// ── Validation ────────────────────────────────────────────────────────────────
const validate = (fields) => {
    const errors = {};
    if (!fields.email?.trim())
        errors.email = 'Email address is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fields.email.trim()))
        errors.email = 'Enter a valid email address';
    if (!fields.password?.trim())
        errors.password = 'Password is required';
    return errors;
};
const Login = () => {
    const [credentials,  setCredentials]  = useState({ email: '', password: '' });
    const [showPassword, setShowPassword] = useState(false);
    const [loading,      setLoading]      = useState(false);
    const [errors,       setErrors]       = useState({});
    const [touched,      setTouched]      = useState({});
    // IMPROVED ERROR DISPLAY: persistent inline alert, separate from the
    // per-field validation errors above — this holds the SERVER's error
    // message (wrong credentials, locked account, expired subscription,
    // etc.), shown until the next submit attempt or manual dismissal.
    const [loginError, setLoginError] = useState('');
    const { login } = useAuth();
    const navigate  = useNavigate();
    // Clear that field's error on change and update value
    const handleChange = (e) => {
        const { name, value } = e.target;
        setCredentials(p => ({ ...p, [name]: value }));
        if (errors[name]) setErrors(p => ({ ...p, [name]: undefined }));
    };
    // Validate single field on blur
    const handleBlur = (e) => {
        const { name } = e.target;
        setTouched(p => ({ ...p, [name]: true }));
        const errs = validate(credentials);
        setErrors(p => ({ ...p, [name]: errs[name] }));
    };
    const handleLogin = async (e) => {
        e.preventDefault();
        setLoginError('');
        // Touch all fields so errors show
        setTouched({ email: true, password: true });
        const errs = validate(credentials);
        if (Object.keys(errs).length > 0) {
            setErrors(errs);
            return;
        }
        setLoading(true);
        try {
            const res = await axiosInstance.post('/auth/login', credentials);
            const { accessToken, user } = res.data.data;
            await login(accessToken, user);
            toast.success(`Welcome back, ${user.first_name}!`);
            navigate(user.role === 'super-admin' ? '/superadmin/dashboard' : '/dashboard');
        } catch (err) {
            const message = err.response?.data?.message || 'Login failed. Please try again.';
            // IMPROVED ERROR DISPLAY: show in both places — the toast for
            // immediate feedback, and the persistent inline alert so the
            // message stays visible rather than vanishing after a few
            // seconds, which matters most for hard-stop errors like an
            // expired company subscription that the person needs to
            // actually read and act on (contact their admin), not just
            // retry immediately the way they would after a typo'd password.
            toast.error(message);
            setLoginError(message);
        } finally {
            setLoading(false);
        }
    };
    return (
        <div style={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg,#f8fafc 0%,#eef2ff 100%)',
            display: 'flex', alignItems: 'center', padding: '24px 16px',
        }}>
            <Container>
                <Row className="justify-content-center">
                    <Col xs={12} sm={10} md={6} lg={4}>
                        {/* Logo */}
                        <div className="text-center mb-4">
                            <Link to="/">
                                <img src="/logo.png" alt="PeoplesDock" style={{ height: 70 }}/>
                            </Link>
                        </div>
                        <Card className="shadow border-0 rounded-4">
                            <Card.Body className="p-4">
                                <h5 className="fw-bold mb-1 text-center">Sign in to your account</h5>
                                <p className="text-muted small text-center mb-4">Enter your credentials to continue</p>
                                {/* IMPROVED ERROR DISPLAY: persistent inline alert */}
                                {loginError && (
                                    <Alert
                                        variant="danger"
                                        className="d-flex align-items-start gap-2 small"
                                        dismissible
                                        onClose={() => setLoginError('')}>
                                        <AlertCircle size={16} className="flex-shrink-0 mt-1"/>
                                        <span>{loginError}</span>
                                    </Alert>
                                )}
                                <Form onSubmit={handleLogin} noValidate>
                                    {/* Email */}
                                    <Form.Group className="mb-3">
                                        <Form.Label className="small fw-bold">Email Address</Form.Label>
                                        <InputGroup hasValidation>
                                            <InputGroup.Text className="bg-white border-end-0">
                                                <Mail size={16} className="text-muted"/>
                                            </InputGroup.Text>
                                            <Form.Control
                                                className="border-start-0 ps-0"
                                                type="email"
                                                name="email"
                                                placeholder="you@company.com"
                                                autoComplete="email"
                                                value={credentials.email}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                isInvalid={!!(touched.email && errors.email)}
                                                isValid={!!(touched.email && !errors.email && credentials.email)}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.email}
                                            </Form.Control.Feedback>
                                        </InputGroup>
                                    </Form.Group>
                                    {/* Password */}
                                    <Form.Group className="mb-3">
                                        <Form.Label className="small fw-bold">Password</Form.Label>
                                        <InputGroup hasValidation>
                                            <InputGroup.Text className="bg-white border-end-0">
                                                <Lock size={16} className="text-muted"/>
                                            </InputGroup.Text>
                                            <Form.Control
                                                className="border-start-0 border-end-0 ps-0"
                                                type={showPassword ? 'text' : 'password'}
                                                name="password"
                                                placeholder="••••••••"
                                                autoComplete="current-password"
                                                value={credentials.password}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                isInvalid={!!(touched.password && errors.password)}
                                                isValid={!!(touched.password && !errors.password && credentials.password)}
                                            />
                                            <Button
                                                variant="outline-secondary"
                                                className="bg-white border-start-0 text-muted"
                                                type="button"
                                                onClick={() => setShowPassword(v => !v)}
                                                style={{ zIndex: 0 }}>
                                                {showPassword ? <EyeOff size={16}/> : <Eye size={16}/>}
                                            </Button>
                                            <Form.Control.Feedback type="invalid">
                                                {errors.password}
                                            </Form.Control.Feedback>
                                        </InputGroup>
                                    </Form.Group>
                                    {/* Forgot password */}
                                    <div className="text-end mb-3">
                                        <Link to="/forgot-password" className="text-primary small text-decoration-none">
                                            Forgot password?
                                        </Link>
                                    </div>
                                    <Button variant="primary" type="submit" className="w-100 fw-bold py-2" disabled={loading}>
                                        {loading ? <><Spinner size="sm" className="me-2"/>Signing in...</> : 'Sign In'}
                                    </Button>
                                </Form>
                                <div className="text-center mt-4">
                                    <small className="text-muted">
                                        Don't have an account?{' '}
                                        <Link to="/register" className="text-primary fw-bold text-decoration-none">Signup</Link>
                                    </small>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};
export default Login;
